import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {ApiService, ValidatorService} from "@amlCore/services";
import {AbstractControl, FormBuilder, FormGroup} from "@angular/forms";
import {CustomValidatorError, Utils} from "@amlCore/utils";
import {PageContentModel, ValidatorParamsModel} from "@amlCore/models";
import {InternalModel} from "../../model";
import {ClientTypeEnum} from "@amlCore/enums";
import {DossierRequest} from "../../../client/models/dossier";

@Injectable()
export class InternalService {


  constructor(private apiService: ApiService,
              private fb: FormBuilder,
              private validatorService: ValidatorService) {
  }


  /**
   * Получить список
   */
  getList(params: any): Observable<PageContentModel<InternalModel>> {
    return this.apiService.get("/api/v1/qref/internal", params, 'id');
  }

  /**
   * Получение записи по id
   * @param id - id
   */
  getInternal(id: string): Observable<InternalModel> {
    return this.apiService.get(`/api/v1/qref/internal/${id}`);
  }

  /**
   * Удаление записии по id
   * @param id - id
   */
  deleteInternal(id: string): Observable<any> {
    return this.apiService.delete(`/api/v1/qref/internal/${id}`);
  }

  /**
   * Создание/обновление
   * @param data - данные
   */
  saveInternal(data: InternalModel): Observable<any> {
    return this.apiService.post('/api/v1/qref/internal', data);
  }

  /**
   * Выгрузка отчета по ошибкам проверки записи перечня на уникальность
   */
  downloadUniqueReport(data: InternalModel) {
    return this.apiService.byte('/api/v1/qref/internal/unique/excel', data);
  }

  getForm(): FormGroup {
    const seriesNumberValidator = new SeriesNumberValidator(this.validatorService);
    const orgNameValidator = new OrgNameValidator(this.validatorService);
    return this.fb.group({
      uchType: [null, this.validatorService.getValidation({
        isReq: true
      })],
      active: false,
      ul: this.fb.group({
        resident: false,
        inn: [null, this.validatorService.getValidation({
          isINN: "INN_KIO"
        })],
        regDate: null,
        created: [null, this.validatorService.getValidation({
          isReq: true
        })],
        reason: [null, this.validatorService.getValidation({
          isReq: true,
          max: 1000
        })],
        orgNameFullRu: null, // валидиация в orgNameValidator
        orgNameRu: null, // валидиация в orgNameValidator
        orgNameFullIn: null, // валидиация в orgNameValidator
        orgNameIn: null, // валидиация в orgNameValidator
        kpp: [null, this.validatorService.getValidation({
          min: 9,
          max: 9
        })],
        ogrn: [null, this.validatorService.getValidation({
          min: 13,
          max: 13,
          isOGRN: true,
        })],
        okpo: [null, this.validatorService.getValidation({
          max: 8,
          isOKPO: true
        })]
      }, {
        validators: orgNameValidator.validator.bind(orgNameValidator)
      }),
      fl: this.fb.group({
        resident: false,
        inn: [null, this.validatorService.getValidation({
          isINN: "INN_12"
        })],
        birthDate: null,
        created: [null, this.validatorService.getValidation({
          isReq: true
        })],
        reason: [null, this.validatorService.getValidation({
          isReq: true,
          max: 1000
        })],
        lastName: [null, this.validatorService.getValidation({
          isReq: true,
          max: 100
        })],
        firstName: [null, this.validatorService.getValidation({
          isReq: true,
          max: 100
        })],
        middleName: [null, this.validatorService.getValidation({
          max: 100
        })],
        documentType: null,
        series: null, // валидация в seriesNumberValidator
        number: null, // валидация в seriesNumberValidator
        organ : [null, this.validatorService.getValidation({
          max: 250
        })],
        dateDoc: null
      }, {
        validators: seriesNumberValidator.validator.bind(seriesNumberValidator)
      }),
      ip: this.fb.group({
        resident: false,
        inn: [null, this.validatorService.getValidation({
          isReq: true,
          isINN: "INN_12"
        })],
        regDate: null,
        birthDate: null,
        created: [null, this.validatorService.getValidation({
          isReq: true
        })],
        reason: [null, this.validatorService.getValidation({
          isReq: true,
          max: 1000
        })],
        lastName: [null, this.validatorService.getValidation({
          isReq: true,
          max: 100
        })],
        firstName: [null, this.validatorService.getValidation({
          isReq: true,
          max: 100
        })],
        middleName: [null, this.validatorService.getValidation({
          max: 100
        })],
        documentType: null,
        series: null, // валидация в seriesNumberValidator
        number: null, // валидация в seriesNumberValidator
        organ : [null, this.validatorService.getValidation({
          max: 250
        })],
        dateDoc: null,
        ogrn: [null, this.validatorService.getValidation({
          min: 15,
          max: 15,
          isOGRN: true,
        })],
        okpo: [null, this.validatorService.getValidation({
          max: 10,
          isOKPO: true
        })]
      }, {
        validators: seriesNumberValidator.validator.bind(seriesNumberValidator)
      }),
      flcp: this.fb.group({
        resident: false,
        inn: [null, this.validatorService.getValidation({
          isReq: true,
          isINN: "INN_12"
        })],
        regDate: null,
        birthDate: null,
        created: [null, this.validatorService.getValidation({
          isReq: true
        })],
        reason: [null, this.validatorService.getValidation({
          isReq: true,
          max: 1000
        })],
        lastName: [null, this.validatorService.getValidation({
          isReq: true,
          max: 100
        })],
        firstName: [null, this.validatorService.getValidation({
          isReq: true,
          max: 100
        })],
        middleName: [null, this.validatorService.getValidation({
          max: 100
        })],
        regNum: [null, this.validatorService.getValidation({
          max: 50
        })],
        documentType: null,
        series: null, // валидация в seriesNumberValidator
        number: null, // валидация в seriesNumberValidator
        organ: [null, this.validatorService.getValidation({
          max: 250
        })],
        dateDoc: null,
      }, {
        validators: seriesNumberValidator.validator.bind(seriesNumberValidator)
      }),
      inbul: this.fb.group({
        resident: false,
        inn: [null, this.validatorService.getValidation({
          isINN: "INN_KIO"
        })],
        regDate: null,
        regNums: [null, this.validatorService.getValidation({
          max: 50
        })],
        opf: null,
        iof:  [null, this.validatorService.getValidation({
          max: 250
        })],
        created: [null, this.validatorService.getValidation({
          isReq: true
        })],
        reason: [null, this.validatorService.getValidation({
          isReq: true,
          max: 1000
        })],
        orgNameFullRu: null, // валидиация в orgNameValidator
        orgNameRu: null, // валидиация в orgNameValidator
        orgNameFullIn: null, // валидиация в orgNameValidator
        orgNameIn: null, // валидиация в orgNameValidator
        okpo: [null, this.validatorService.getValidation({
          max: 8,
          isOKPO: true
        })]
      }, {
        validators: orgNameValidator.validator.bind(orgNameValidator)
      }),
      adress: this.getAdressForm()
    });
  }

  getAdressForm(): FormGroup {
    return this.fb.group({
      id: null,
      country: null,
      index: [null, this.validatorService.getValidation({
        max: 30
      })],
      region: null,
      area: [null, this.validatorService.getValidation({
        max: 250
      })],
      city: [null, this.validatorService.getValidation({
        max: 250
      })],
      locality: [null, this.validatorService.getValidation({
        max: 250
      })],
      street: [null, this.validatorService.getValidation({
        max: 250
      })],
      house: [null, this.validatorService.getValidation({
        max: 60
      })],
      building: [null, this.validatorService.getValidation({
        max: 50
      })],
      apartment: [null, this.validatorService.getValidation({
        max: 100
      })]
    });
  }

 /**
   * Столбец "Наименование/ФИО"
   * @param item - строка
   */
  getName(item: InternalModel): string {
    return [ClientTypeEnum.INDIVIDUAL_ENTREPRENEUR,
      ClientTypeEnum.INDIVIDUAL_PERSON,
      ClientTypeEnum.PRIVATE_PRACTITIONER
    ].includes(item.uchType) ? [item.lastName, item.firstName, item.middleName].filter(Boolean).join(" ") :
      [ClientTypeEnum.LEGAL_ENTITY,
        ClientTypeEnum.FOREIGN_STRUCTURE].includes(item.uchType) ? getName1_5() : '';

    // Для 1 и 5 выводится одно из "orgNameRu", "orgNameFullRu", "orgNameIn", "orgNameFullIn"
    function getName1_5(): string {
      return item.orgNameRu ? item.orgNameRu :
        item.orgNameFullRu ? item.orgNameFullRu :
          item.orgNameIn ? item.orgNameIn :
            item.orgNameFullIn ? item.orgNameFullIn : '';
    }
  }
}

/**
 * Валидация для полей серия и номер по типу документа
 * Выставляем различные длинны полей
 */
class SeriesNumberValidator {

  constructor(protected validationSrv: ValidatorService) {
  }

  validator(control: AbstractControl): { [key: string]: CustomValidatorError } | null {
    const seriesField = control.get('series');
    const numberField = control.get('number');
    const documentTypeField = control.get('documentType');
    const seriesValidator = {
      max: 50
    } as ValidatorParamsModel;
    const numberValidator = {
      max: 50
    } as ValidatorParamsModel;

    if (!seriesField || !numberField || !documentTypeField) {
      return null;
    }
    if (documentTypeField.value && documentTypeField.value === "1") {
      seriesValidator.max = 4;
      seriesValidator.min = 4;
      numberValidator.max = 6;
      numberValidator.min = 6;
    }
    seriesField.setValidators(this.validationSrv.getValidation(seriesValidator));
    seriesField.updateValueAndValidity({onlySelf: true});
    numberField.setValidators(this.validationSrv.getValidation(numberValidator));
    numberField.updateValueAndValidity({onlySelf: true});
    return null;
  }
}

/**
 * Валидация для полей orgName
 * должно быть заполнено одно из полей orgNameFullRu или orgNameRu или orgNameFullIn или orgNameIn.
 * Выставляем различные длинны полей
 */
class OrgNameValidator {

  constructor(protected validationSrv: ValidatorService) {
  }

  validator(control: AbstractControl): { [key: string]: CustomValidatorError } | null {
    const orgNameFullRuField = control.get('orgNameFullRu');
    const orgNameRuField = control.get('orgNameRu');
    const orgNameFullInField = control.get('orgNameFullIn');
    const orgNameInField = control.get('orgNameIn');
    const orgNameFullRuValidator = {
      max: 2000
    } as ValidatorParamsModel;
    const orgNameRuValidator = {
      max: 2000
    } as ValidatorParamsModel;
    const orgNameFullInValidator = {
      max: 2000
    } as ValidatorParamsModel;
    const orgNameInValidator = {
      max: 2000
    } as ValidatorParamsModel;
    if (!orgNameFullRuField || !orgNameRuField || !orgNameFullInField || !orgNameInField) {
      return null;
    }
    if (checkEmpty(orgNameFullRuField) && checkEmpty(orgNameRuField) && checkEmpty(orgNameFullInField) && checkEmpty(orgNameInField)) {
      orgNameFullRuValidator.isReq = true;
      orgNameRuValidator.isReq = true;
      orgNameFullInValidator.isReq = true;
      orgNameInValidator.isReq = true;
    }
    orgNameFullRuField.setValidators(this.validationSrv.getValidation(orgNameFullRuValidator));
    orgNameFullRuField.updateValueAndValidity({onlySelf: true});
    orgNameRuField.setValidators(this.validationSrv.getValidation(orgNameRuValidator));
    orgNameRuField.updateValueAndValidity({onlySelf: true});
    orgNameFullInField.setValidators(this.validationSrv.getValidation(orgNameFullInValidator));
    orgNameFullInField.updateValueAndValidity({onlySelf: true});
    orgNameInField.setValidators(this.validationSrv.getValidation(orgNameInValidator));
    orgNameInField.updateValueAndValidity({onlySelf: true});
    return null;

    function checkEmpty(item) {
      return !(item || {}).value || item.value && item.value === "";
    }
  }
}
